import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '../../CommonComponents/OPL/Box/Box';
import { Button } from '../../CommonComponents/OPL/Button/Button';
import { Card } from '../../CommonComponents/OPL/Card/Card';
import { Text } from '../../CommonComponents/OPL/Text/Text';
import { ScrollHereOnce } from '../../CommonComponents/ScrollHereOnce';
import { isConsumerArea } from '../../CommonSelectors/CommonAreaSelector';
import { ClientFooter } from '../../ConsumerArea/components/Footer/FooterView';
import { useClientId } from '../../_hooks/useClientId';
import { useDocumentTitle } from '../../_hooks/useDocumentTitle';
import { useRouteParams } from '../../_hooks/useRouteParams';
import { BEFI20_DISABLE_ERROR_REGEX, useSurveyResultsQuery } from '../api/useSurveyResultsQuery';
import { ErrorState } from '../components/ErrorState';
import { useIsMobile } from '../../_hooks/useIsMobile';
import { Wrapper as MainWrapper } from '../BeFi20.styles';
import { ShareSection } from '../Overview/components/ShareSection/ShareSection';
import { LoadingScreen } from '../Survey/components/LoadingScreen/LoadingScreen';
import { ComparingCategory } from './components/ComparingCategory/ComparingCategory';
import { Confetti } from './components/Confetti/Confetti';
import { IndividualCategory } from './components/IndividualCategory/IndividualCategory';
import { JointAccountsInvite } from './components/JointAccountsInvite/JointAccountsInvite';
import { ShareModal } from './components/ShareModal/ShareModal';
import { InviteType } from './components/ShareModal/ShareModal.meta';
import { ShareOptionsModal } from './components/ShareOptionsModal/ShareOptionsModal';
import { Wrapper } from './Output.styles';
import { TopBar } from './components/TopBar/TopBar';
import { Hero } from './components/Hero/Hero';
import { FloatingMenu } from './components/FloatingMenu/FloatingMenu';

export const Output = () => {
  useDocumentTitle('BeFi20 Results');

  const isClientArea = useSelector(isConsumerArea);

  const routeParams = useRouteParams<{
    compareToId: string;
    isFromSurvey: string;
    isInitiallyComparing: string;
  }>();

  const compareToId = routeParams?.compareToId;
  const isFromSurvey = routeParams?.isFromSurvey === 'true';

  const isInitiallyComparing = routeParams?.isInitiallyComparing === 'true';

  const queryInfo = useSurveyResultsQuery({ compareToId });

  const isMobile = useIsMobile();

  const clientId = useClientId();

  const [_isComparing, setIsComparing] = useState(isInitiallyComparing);
  const [isShareOptionsModalOpen, setIsShareOptionsModalOpen] = useState(false);
  const [inviteType, setInviteType] = useState<InviteType | null>(null);

  if (queryInfo.isLoading) {
    return (
      <LoadingScreen
        text="Loading Your Financial Persona"
        shouldAnimate={false}
        isClientArea={isClientArea}
      />
    );
  }

  if (queryInfo.error) {
    const canRetry = !BEFI20_DISABLE_ERROR_REGEX.test(queryInfo.error.message);

    return (
      <ErrorState
        isMobile={isMobile}
        message={queryInfo.error.message}
        onAction={canRetry ? queryInfo.refetch : undefined}
      />
    );
  }

  if (!queryInfo.data) {
    return null;
  }

  const { comparisonResult, individualResults, categoryInfo, comparisons } = queryInfo.data;

  const hasComparison = comparisonResult !== null;

  const isComparing = hasComparison && _isComparing;

  return (
    <MainWrapper>
      <ScrollHereOnce key={`key-${isComparing}`} options={false} />

      <TopBar clientId={clientId} isMobile={isMobile} />

      {isFromSurvey && <Confetti />}

      <Wrapper
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: isFromSurvey ? 2.5 : 0.3 }}
      >
        <Card p={isMobile ? '16' : '32'} boxShadow={isMobile ? '0' : '500'} borderRadius="xxl">
          <Hero
            isComparing={isComparing}
            individualResults={individualResults}
            comparisonResult={comparisonResult}
          />

          {isComparing && (
            <Text
              textAlign="center"
              variant="smallCaps"
              color="neutral.600"
              fontSize="small"
              lineHeight="small"
              my="24"
            >
              Category Alignment Breakdown
            </Text>
          )}

          {!isComparing && (
            <JointAccountsInvite onInvite={() => setInviteType(InviteType.Compare)} />
          )}

          {isComparing && comparisonResult
            ? Object.entries(comparisonResult.comparedScores).map(
                ([categoryName, categoryResult]) => {
                  return (
                    <ComparingCategory
                      key={categoryName}
                      categoryInfo={categoryInfo}
                      comparisonItems={categoryResult.comparisonItems}
                      compatibilityScore={categoryResult.score}
                      description={categoryResult.description}
                      label={categoryName}
                      mb="48"
                      primaryRespondentScore={individualResults.categories[categoryName].score}
                      secondaryRespondentName={comparisonResult.partnerName}
                      secondaryRespondentScore={categoryResult.partnerScore}
                    />
                  );
                },
              )
            : Object.entries(individualResults.categories).map(([categoryName, categoryResult]) => {
                return (
                  <IndividualCategory
                    key={categoryName}
                    categoryInfo={categoryInfo}
                    description={categoryResult.description}
                    label={categoryName}
                    primaryRespondentScore={categoryResult.score}
                    mb="48"
                  />
                );
              })}

          {!isComparing && (
            <Box display="flex" justifyContent="center" alignItems="center" my="56">
              <Button
                variant="link"
                rightIcon="far fa-redo"
                onClick={() => {
                  ADV.util.Router.go2('BeFi20Workflow', { clientId });
                }}
              >
                Retake Assessment
              </Button>
            </Box>
          )}

          <ShareSection />

          {inviteType !== null && (
            <ShareModal inviteType={inviteType} onClose={() => setInviteType(null)} />
          )}

          <ShareOptionsModal
            isOpen={isShareOptionsModalOpen}
            onClose={() => setIsShareOptionsModalOpen(false)}
            onCompareClick={() => {
              setInviteType(InviteType.Compare);
              setIsShareOptionsModalOpen(false);
            }}
            onShareClick={() => {
              setInviteType(InviteType.Share);
              setIsShareOptionsModalOpen(false);
            }}
          />
        </Card>
      </Wrapper>

      {comparisons.length > 0 && comparisonResult && (
        <FloatingMenu
          isFromSurvey={isFromSurvey}
          isComparing={isComparing}
          setIsComparing={setIsComparing}
          comparisons={comparisons}
          comparisonResult={comparisonResult}
          compareToId={compareToId}
          onShareClick={() => setIsShareOptionsModalOpen(true)}
        />
      )}

      {isClientArea && <ClientFooter />}
    </MainWrapper>
  );
};
